import { create } from "zustand"
import { AccessData, CompanyData, WhitelistEntry } from "./data"
import { addCom, inviteOwner, removeCompany, retrieveComs, updateCom, toggleDeviceApproval, toggleIpWhitelist, getCompanyIpWhitelist } from "./actions"
import { adminInitiateUserPasswordResetAPI } from "../users/actions"
import { Dictionary } from "../users/store"
import { GLOBAL_APP, OWNER_ROLE } from "../../constants"

type CompaniesProps = {
    loading: boolean
    companies: CompanyData[] | null
    totalCompanies: number
    currentPage: number
    setCurrentPage: (page: number) => void
    retrieveCompanies: (page?: number) => Promise<string>
    addCompany: (id: string, name: string) => Promise<string>
    removeCompany: (id: string) => Promise<string>
    updateCompany: (com: CompanyData) => Promise<string>
    inviteOwner: (com: string, email: string) => Promise<string>
    toggleDeviceApproval: (enabled: boolean) => Promise<string>
    toggleIpWhitelist: (enabled: boolean) => Promise<string>
    whitelist: WhitelistEntry[] | null
    loadingWhitelist: boolean
    retrieveIpWhitelist: () => Promise<string>
    unblockOwner: (username: string) => Promise<boolean>
}

export const useCompanies = create<CompaniesProps>((set, get) => ({
    loading: false,
    companies: null,
    totalCompanies: 0,
    currentPage: 1,
    whitelist: [],
    loadingWhitelist: false,
    setCurrentPage(page) {
        set({ currentPage: page })
    },
    async retrieveCompanies(page = 1) {
        if (get().loading) return ""
        set(state => ({ loading: true, currentPage: page }))

        const offset = (page - 1) * 30
        const res = await retrieveComs(offset)

        const coms = res.Msg.coms as CompanyData[]
        const owners = res.Msg.owners as Dictionary<AccessData>
        const total = res.Msg.total || (coms ? coms.length : 0)

        if (coms) {
            for (const com of coms) {
                com.access = owners[com.id!]!
            }
        }
        //console.log(coms)
        set(state => ({ loading: false, companies: coms, totalCompanies: total }))
        return ""
    },
    async addCompany(id, name) {
        const newcom = { id: id, name: name }
        const code = await addCom(newcom)
        if (code === "200") {
            // Refresh the current page to show the new company
            await get().retrieveCompanies(get().currentPage)
        }
        return code
    },
    async removeCompany(id) {
        const code = await removeCompany(id)
        if (code === "200") {
            // Refresh the current page after deletion
            await get().retrieveCompanies(get().currentPage)
        }
        return code
    },
    async updateCompany(com) {
        const code = await updateCom(com.id!, com.name!, com.img!)
        if (code === "200") {
            const coms = [...get().companies!]
            const index = coms.findIndex(item => item.id === com.id)
            if (index! >= 0) {
                coms[index] = com
                set(state => ({ companies: coms }))
            }
        }
        return code
    },
    async inviteOwner(com, email) {
        const code = await inviteOwner(com, email)
        const coms = [...get().companies!]
        const index = coms.findIndex(item => item.id === com)
        if (code === "200") {
            coms[index].access = { email: email, status: "pending", roles: [OWNER_ROLE], app: GLOBAL_APP, username: "" }
            coms[index].owner = email
            set(state => ({ companies: coms }))
        }
        return code
    },
    async toggleDeviceApproval(enabled) {
        const code = await toggleDeviceApproval(enabled)
        return code
    },
    async toggleIpWhitelist(enabled) {
        const code = await toggleIpWhitelist(enabled)
        return code
    },
    async retrieveIpWhitelist() {
        set({ loadingWhitelist: true })
        try {
            const res = await getCompanyIpWhitelist()
            if (res.Code === "200") {
                set({ whitelist: res.Msg.whitelist || [] })
                return "200"
            }
            return res.Code
        } catch (err) {
            console.error(err)
            return "error"
        } finally {
            set({ loadingWhitelist: false })
        }
    },
    async unblockOwner(username) {
        try {
            await adminInitiateUserPasswordResetAPI(username)
            return true
        } catch (err) {
            console.error(err)
            return false
        }
    }
}))